import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const FreelancerPayoutSummaryPage = () => {
  const [payoutSummary, setPayoutSummary] = useState([]);
  const [filteredSummary, setFilteredSummary] = useState([]);
  const [searchFreelancerId, setSearchFreelancerId] = useState('');
  const [withdrawableFilter, setWithdrawableFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchPayoutSummary = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-payout-summary`);
        setPayoutSummary(response.data);
        setFilteredSummary(response.data);
      } catch (err) {
        setError('Failed to fetch payout summary.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayoutSummary();
  }, [apiBaseURL]);

  useEffect(() => {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    let filtered = payoutSummary.filter(summary => {
      const updated = new Date(summary.last_updated);
      return updated >= firstDayOfMonth && updated <= now;
    });

    if (searchFreelancerId.trim() !== '') {
      filtered = filtered.filter(summary =>
        String(summary.freelancer_id).toLowerCase().includes(searchFreelancerId.toLowerCase())
      );
    }

    if (withdrawableFilter === 'Zero') {
      filtered = filtered.filter(summary => Number(summary.earnings_yet_to_withdraw) === 0);
    } else if (withdrawableFilter === 'Non-zero') {
      filtered = filtered.filter(summary => Number(summary.earnings_yet_to_withdraw) > 0);
    }

    setFilteredSummary(filtered);
  }, [searchFreelancerId, withdrawableFilter, payoutSummary]);

  if (loading) return <div>Loading payout summary...</div>;
  if (error) return <div>{error}</div>;

  const totalEarnings = filteredSummary.reduce((sum, s) => sum + Number(s.total_earnings || 0), 0);
  const totalWithdrawn = filteredSummary.reduce((sum, s) => sum + Number(s.earnings_already_withdrawn || 0), 0);
  const totalWithdrawable = filteredSummary.reduce((sum, s) => sum + Number(s.earnings_yet_to_withdraw || 0), 0);

  const columns = [
    { label: 'Freelancer ID', key: 'freelancer_id' },
    { label: 'Freelancer Name', key: 'freelancer_name' },
    { label: 'Total Earnings (₹)', key: 'total_earnings' },
    { label: 'Already Withdrawn (₹)', key: 'earnings_already_withdrawn' },
    { label: 'Yet to Withdraw (₹)', key: 'earnings_yet_to_withdraw' },
    { label: 'Last Updated', key: 'last_updated' },
  ];

  const data = filteredSummary.map(summary => ({
    freelancer_id: summary.freelancer_id,
    freelancer_name: summary.freelancer_name || 'N/A',
    total_earnings: Number(summary.total_earnings).toFixed(2),
    earnings_already_withdrawn: Number(summary.earnings_already_withdrawn || 0).toFixed(2),
    earnings_yet_to_withdraw: Number(summary.earnings_yet_to_withdraw).toFixed(2),
    last_updated: summary.last_updated ? new Date(summary.last_updated).toLocaleString() : 'N/A',
  }));

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, idx) => (
              <th key={idx} className="px-3 py-2 text-left">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-3 text-center text-gray-500">No payout data found.</td>
            </tr>
          ) : (
            data.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b">
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 whitespace-nowrap">{row[col.key]}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const downloadExcel = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;
    const downloadTime = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}, ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

    const filename = `Freelancer_Payout_Summary_${timestamp}.xlsx`;

    const sheetData = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Payout Summary'],
      [`Downloaded on: ${downloadTime}`],
      [],
      columns.map(col => col.label),
      ...data.map(row => columns.map(col => row[col.key]))
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PayoutSummary");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, filename);
  };

  return (
    <div className="flex-1 p-6 bg-gray-50 ml-64">
      <h2 className="text-2xl font-semibold mb-5">Freelancer Payout Summary</h2>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div>
          <label className="font-medium">Search Freelancer ID:</label>
          <input
            type="text"
            value={searchFreelancerId}
            onChange={e => setSearchFreelancerId(e.target.value)}
            className="border rounded px-3 py-1 ml-2"
            placeholder="Type freelancer id"
          />
        </div>

        <div>
          <label className="font-medium">Withdrawable Balance:</label>
          <select
            value={withdrawableFilter}
            onChange={e => setWithdrawableFilter(e.target.value)}
            className="border rounded px-3 py-1 ml-2"
          >
            <option value="All">All</option>
            <option value="Zero">Zero</option>
            <option value="Non-zero">Non-zero</option>
          </select>
        </div>

        <div>
          <button
            onClick={downloadExcel}
            className="bg-green-600 text-white px-4 py-2 rounded shadow hover:bg-green-700 transition"
          >
            Download Excel
          </button>
        </div>
      </div>

      {/* Summary Box */}
      <div className="w-full mt-6 border border-gray-300 rounded-lg bg-white shadow-sm p-5">
        <h3 className="text-xl font-semibold mb-4">📊 Summary Overview</h3>
        <div className="space-y-3">
          <div className="text-gray-800 text-lg font-medium">
            <span className="font-bold">{filteredSummary.length}</span> : 📊 Total Count
          </div>
          <div className="text-green-800 text-lg font-medium">
            <span className="font-bold">₹{totalEarnings.toFixed(2)}</span> : 💰 total_earnings by all Freelancers
          </div>
          <div className="text-blue-800 text-lg font-medium">
            <span className="font-bold">₹{totalWithdrawn.toFixed(2)}</span> : 📤 earnings_already_withdrawn by all Freelancers
          </div>
          <div className="text-gray-800 text-lg font-medium">
            <span className="font-bold">₹{totalWithdrawable.toFixed(2)}</span> : 🏦 earnings_yet_to_withdraw by all Freelancers
          </div>
        </div>
      </div>

      {/* Data Table */}
      <Table columns={columns} data={data} />
    </div>
  );
};

export default FreelancerPayoutSummaryPage;